/**
 * ============================================================================
 * IoT 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 IoT（物联网）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createIoTApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * ⚠️ 重要：路径引用规范
 * - ✅ 必须从 API_ROUTES 引用路径（v3.13.0+ 强制要求）
 * - ❌ 禁止硬编码路径字符串
 * - ❌ 禁止字符串拼接路径
 * 
 * @module api/iot/factory
 * @description IoT（物联网）模块的 API 工厂函数
 * @generated 2025-12-06
 * @updated 2025-12-06 - 修复为使用 API_ROUTES 引用
 * ============================================================================
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  IoTVendor,
  IoTDevice,
  IoTDiscoveredDevice,
  IoTDeviceSnapshot,
  IoTDeviceAlarm,
  IoTStatisticsOverview,
  VendorOption,
  CreateVendorRequest,
  UpdateVendorRequest,
  BindDiscoveredDeviceRequest,
  IgnoreDiscoveredDeviceRequest,
  ControlDeviceRequest,
  AcknowledgeAlarmRequest,
  BatchDeviceDataRequest,
  VendorListQueryParams,
  DeviceListQueryParams,
  DiscoveredDeviceListQueryParams,
  AlarmListQueryParams,
  DeviceHistoryQueryParams,
  StatisticsQueryParams
} from '../../types/iot'

import type { PaginationResult } from '../../types/api'
import type { IRequestClient } from '../core/client'

// ============================================================================
// 导入 API 路由定义（v3.13.0+ 强制要求）
// ============================================================================
import { API_ROUTES } from '../routes'

type QueryParams = Record<string, any> | undefined

function cleanParams(params: QueryParams): QueryParams {
  if (!params) return undefined

  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  )
}

function withPerPage(params: QueryParams): QueryParams {
  if (!params) return undefined

  const next = { ...params }
  if (next.per_page === undefined && next.page_size === undefined && next.pageSize !== undefined) {
    next.per_page = next.pageSize
  }
  delete next.pageSize

  return cleanParams(next)
}

function withPageSize(params: QueryParams): QueryParams {
  if (!params) return undefined

  const next = { ...params }
  if (next.page_size === undefined && next.per_page !== undefined) {
    next.page_size = next.per_page
  }
  if (next.page_size === undefined && next.pageSize !== undefined) {
    next.page_size = next.pageSize
  }
  delete next.pageSize
  delete next.per_page

  return cleanParams(next)
}

function withGatewayHistoryParams(params: DeviceHistoryQueryParams): QueryParams {
  const next: QueryParams = { ...params }

  if (next.start === undefined && next.start_time !== undefined) {
    next.start = next.start_time
  }
  if (next.end === undefined && next.end_time !== undefined) {
    next.end = next.end_time
  }

  delete next.start_time
  delete next.end_time
  delete next.device_sn

  return withPageSize(next)
}

// ============================================================================
// IoT API 工厂函数
// ============================================================================

/**
 * 创建 IoT 模块的 API 方法集合
 */
export function createIoTApi(client: IRequestClient) {
  return {
    // ========================================================================
    // 厂家管理 API
    // ========================================================================
    
    vendor: {
      /**
       * 获取厂家列表（分页）
       */
      list(params?: VendorListQueryParams) {
        return client.get<PaginationResult<IoTVendor>>(
          API_ROUTES.iot.list,
          { params: withPerPage(params) }
        )
      },
      
      /**
       * 获取厂家选项（下拉框）
       */
      options() {
        return client.get<VendorOption[]>(API_ROUTES.iot.vendorsOptions)
      },
      
      /**
       * 获取厂家详情
       */
      detail(id: number) {
        return client.get<IoTVendor>(API_ROUTES.iot.detail(id))
      },
      
      /**
       * 创建厂家
       */
      create(data: CreateVendorRequest) {
        return client.post<IoTVendor>(API_ROUTES.iot.vendors, data)
      },
      
      /**
       * 更新厂家
       */
      update(id: number, data: UpdateVendorRequest) {
        return client.put<IoTVendor>(API_ROUTES.iot.iotVendors(id), data)
      },
      
      /**
       * 删除厂家
       */
      delete(id: number) {
        return client.delete<void>(API_ROUTES.iot.delete(id))
      }
    },
    
    // ========================================================================
    // 绑定设备状态 API
    // ========================================================================
    
    device: {
      /**
       * 获取已绑定 IoT SN 的 MES 设备列表（分页）。
       *
       * 设备主数据由 Equipment 模块维护；此接口只返回绑定设备的 IoT 状态。
       */
      list(params?: DeviceListQueryParams) {
        return client.get<PaginationResult<IoTDevice>>(
          API_ROUTES.iot.devices,
          { params: withPageSize(params) }
        )
      },
      
      /**
       * 获取设备详情
       */
      detail(sn: string) {
        return client.get<IoTDevice>(API_ROUTES.iot.iotDevices(sn))
      }
    },

    // ========================================================================
    // 网关发现设备 API
    // ========================================================================

    discovery: {
      /**
       * 获取网关发现设备列表
       */
      list(params?: DiscoveredDeviceListQueryParams) {
        return client.get<PaginationResult<IoTDiscoveredDevice>>(
          API_ROUTES.iot.discoveredDevices,
          { params: withPageSize(params) }
        )
      },

      /**
       * 获取发现设备详情
       */
      detail(sn: string) {
        return client.get<IoTDiscoveredDevice>(
          API_ROUTES.iot.discoveredDevice(sn)
        )
      },

      /**
       * 绑定发现设备到 MES 设备
       */
      bind(sn: string, data: BindDiscoveredDeviceRequest) {
        return client.post<IoTDiscoveredDevice>(
          API_ROUTES.iot.bindDiscoveredDevice(sn),
          data
        )
      },

      /**
       * 忽略发现设备
       */
      ignore(sn: string, data?: IgnoreDiscoveredDeviceRequest) {
        return client.post<IoTDiscoveredDevice>(
          API_ROUTES.iot.ignoreDiscoveredDevice(sn),
          data || {}
        )
      }
    },
    
    // ========================================================================
    // 实时数据 API（代理 Go 网关）
    // ========================================================================
    
    data: {
      /**
       * 获取设备列表（含实时状态）
       */
      devices(params?: DeviceListQueryParams) {
        return client.get<PaginationResult<IoTDevice>>(
          API_ROUTES.iot.devices,
          { params: withPageSize(params) }
        )
      },
      
      /**
       * 获取设备实时数据
       */
      realtime(sn: string) {
        return client.get<IoTDeviceSnapshot>(API_ROUTES.iot.devicesData(sn))
      },
      
      /**
       * 批量获取设备数据
       */
      batchData(data: BatchDeviceDataRequest) {
        return client.post<Record<string, IoTDeviceSnapshot>>(
          API_ROUTES.iot.data,
          data
        )
      },
      
      /**
       * 获取设备历史数据
       */
      history(sn: string, params: DeviceHistoryQueryParams) {
        return client.get<PaginationResult<IoTDeviceSnapshot>>(
          API_ROUTES.iot.devicesHistory(sn),
          { params: withGatewayHistoryParams(params) }
        )
      },
      
      /**
       * 发送控制指令
       */
      control(sn: string, data: ControlDeviceRequest) {
        return client.post<{ success: boolean; message?: string }>(
          API_ROUTES.iot.control(sn),
          data
        )
      },
      
      /**
       * 获取设备状态
       */
      status(sn: string) {
        return client.get<IoTDeviceSnapshot>(API_ROUTES.iot.devicesStatus(sn))
      }
    },
    
    // ========================================================================
    // 报警管理 API
    // ========================================================================
    
    alarm: {
      /**
       * 获取报警列表（分页）
       */
      list(params?: AlarmListQueryParams) {
        return client.get<PaginationResult<IoTDeviceAlarm>>(
          API_ROUTES.iot.iotAlarms,
          { params: withPageSize(params) }
        )
      },
      
      /**
       * 获取活跃报警
       */
      active(params?: Omit<AlarmListQueryParams, 'status'>) {
        return client.get<PaginationResult<IoTDeviceAlarm>>(
          API_ROUTES.iot.active,
          { params: withPageSize(params) }
        )
      },
      
      /**
       * 确认报警
       */
      acknowledge(id: number, data?: AcknowledgeAlarmRequest) {
        return client.post<IoTDeviceAlarm>(
          API_ROUTES.iot.acknowledge(id),
          data || {}
        )
      },
      
      /**
       * 获取设备报警
       */
      deviceAlarms(sn: string, params?: AlarmListQueryParams) {
        return client.get<PaginationResult<IoTDeviceAlarm>>(
          API_ROUTES.iot.devicesAlarms(sn),
          { params: withPageSize(params) }
        )
      }
    },
    
    // ========================================================================
    // 统计分析 API
    // ========================================================================
    
    statistics: {
      /**
       * 获取统计概览
       */
      overview(params?: StatisticsQueryParams) {
        return client.get<IoTStatisticsOverview>(
          API_ROUTES.iot.statisticsOverview,
          { params }
        )
      }
    },
    
    // ========================================================================
    // 健康检查 API
    // ========================================================================
    
    /**
     * 检查 Go 网关健康状态
     */
    health() {
      return client.get<{ status: string; message: string }>(
        API_ROUTES.iot.iotHealth
      )
    }
  }
}

// ============================================================================
// 类型导出
// ============================================================================

/**
 * IoT API 方法集合类型
 */
export type IoTApi = ReturnType<typeof createIoTApi>
